import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  HicapsConnectService,
  HicapsConnectV2Adapter,
  HicapsConnectV2Routing,
  HicapsConnectV2SyncService,
  ISelectedHicapsConnectTerminal,
} from '@principle-theorem/ng-payments';
import {
  GlobalStoreService,
  StafferDisplayComponent,
  StatusLabelComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  LoaderButtonComponent,
  MapPipe,
} from '@principle-theorem/ng-shared';
import {
  HicapsConnectMerchant,
  HicapsConnectTerminal,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IBridgeDevice,
  IHicapsConnectMerchant,
  IHicapsConnectTerminalV2,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, WithRef, shareReplayCold } from '@principle-theorem/shared';
import { compact } from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  HicapsConnectV2EditMerchantDialogComponent,
  IHicapsV2EditMerchantRequest,
} from './hicaps-connect-v2-edit-merchant-dialog/hicaps-connect-v2-edit-merchant-dialog.component';
import {
  HicapsConnectV2EditTerminalDialogComponent,
  IHicapsV2EditTerminalRequest,
} from './hicaps-connect-v2-edit-terminal-dialog/hicaps-connect-v2-edit-terminal-dialog.component';
import { HICAPS_SETTINGS_NOTICE_MS } from './hicaps-connect-v2-settings-notice';
import {
  IBridgeOption,
  ITerminalWithMerchants,
} from './terminal-with-merchants';

@Component({
  selector: 'pr-practice-hicaps-connect-v2-settings',
  templateUrl: './practice-hicaps-connect-v2-settings.component.html',
  styleUrls: ['./practice-hicaps-connect-v2-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    LoaderButtonComponent,
    MapPipe,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatTooltipModule,
    StafferDisplayComponent,
    StatusLabelComponent,
  ],
})
export class PracticeHicapsConnectV2SettingsComponent {
  private readonly _syncService = inject(HicapsConnectV2SyncService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _hicapsConnect = inject(HicapsConnectService);
  private readonly _dialog = inject(MatDialog);
  private readonly _basicDialog = inject(BasicDialogService);
  private readonly _practice$: Observable<WithRef<IPractice>>;

  readonly globalStore = inject(GlobalStoreService);
  readonly practice = input.required<WithRef<IPractice>>();
  readonly isSyncing = signal(false);
  readonly merchantColumns: string[] = [
    'name',
    'merchantId',
    'providerName',
    'practitioner',
    'actions',
  ];
  readonly terminalColumns: string[] = [
    'name',
    'terminalId',
    'servingBridge',
    'status',
    'actions',
  ];
  readonly merchants$: Observable<WithRef<IHicapsConnectMerchant>[]>;
  readonly hasMerchants$: Observable<boolean>;
  readonly bridges$: Observable<WithRef<IBridgeDevice>[]>;
  readonly terminalsWithMerchants$: Observable<ITerminalWithMerchants[]>;
  readonly hasTerminalsWithMerchants$: Observable<boolean>;

  /**
   * The Firestore-backed streams are shared: the template reads `merchants$`
   * from both the merchant table and the terminal table, and each bare
   * `| async` would otherwise open its own listener.
   */
  constructor() {
    this._practice$ = toObservable(this.practice);

    this.merchants$ = this._practice$.pipe(
      switchMap((practice) => Practice.hicapsConnectMerchants$(practice)),
      shareReplayCold()
    );
    this.hasMerchants$ = this.merchants$.pipe(
      map((merchants) => merchants.length > 0)
    );

    this.bridges$ = this._practice$.pipe(
      switchMap((practice) => Practice.bridgeDevices$(practice)),
      shareReplayCold()
    );

    const terminals$ = this._practice$.pipe(
      switchMap((practice) => Practice.hicapsConnectTerminalsV2$(practice))
    );

    this.terminalsWithMerchants$ = combineLatest([
      terminals$,
      this.bridges$,
    ]).pipe(
      map(([terminals, bridges]) =>
        terminals.map((terminal) =>
          this._buildTerminalWithMerchants(terminal, bridges)
        )
      ),
      shareReplayCold()
    );

    this.hasTerminalsWithMerchants$ = this.terminalsWithMerchants$.pipe(
      map((terminals) => terminals.length > 0)
    );
  }

  readonly getMerchantDisplay = (
    merchant: WithRef<IHicapsConnectMerchant> | undefined
  ): string => HicapsConnectMerchant.displayName(merchant);

  async syncAll(practice: WithRef<IPractice>): Promise<void> {
    try {
      this.isSyncing.set(true);
      await this._syncService.syncAllBridges(practice);
      this._snackBar.open('Sync complete', 'OK', {
        duration: HICAPS_SETTINGS_NOTICE_MS,
      });
    } catch {
      this._snackBar.open('Sync failed', 'OK', {
        duration: HICAPS_SETTINGS_NOTICE_MS,
      });
    } finally {
      this.isSyncing.set(false);
    }
  }

  async toggleMerchantDisabled(
    merchant: WithRef<IHicapsConnectMerchant>,
    disabled: boolean
  ): Promise<void> {
    try {
      await Firestore.patchDoc(merchant.ref, { disabled });
    } catch {
      this._snackBar.open('Merchant update failed', 'OK', {
        duration: HICAPS_SETTINGS_NOTICE_MS,
      });
    }
  }

  async disableTerminal(
    terminalWithMerchants: ITerminalWithMerchants
  ): Promise<void> {
    const confirmed = await this._basicDialog.confirm({
      title: 'Disable Terminal',
      prompt: `Are you sure you want to disable ${terminalWithMerchants.name}? It won't be offered when taking payments, and you can re-enable it at any time.`,
      submitLabel: 'Disable',
      submitColor: 'warn',
    });
    if (!confirmed) {
      return;
    }
    await this._setTerminalDisabled(terminalWithMerchants, true);
  }

  async enableTerminal(
    terminalWithMerchants: ITerminalWithMerchants
  ): Promise<void> {
    await this._setTerminalDisabled(terminalWithMerchants, false);
  }

  openEditMerchantDialog(merchant: WithRef<IHicapsConnectMerchant>): void {
    this._dialog.open<
      HicapsConnectV2EditMerchantDialogComponent,
      IHicapsV2EditMerchantRequest
    >(
      HicapsConnectV2EditMerchantDialogComponent,
      DialogPresets.medium({ data: { merchant } })
    );
  }

  async testTerminal(
    terminalWithMerchants: ITerminalWithMerchants,
    bridges: WithRef<IBridgeDevice>[],
    merchants: WithRef<IHicapsConnectMerchant>[]
  ): Promise<void> {
    const selected = this._resolveSelectedTerminal(
      terminalWithMerchants,
      bridges,
      merchants
    );
    if (!selected) {
      return;
    }
    await this._hicapsConnect.testTerminal(selected);
  }

  async viewLogsForTerminal(
    terminalWithMerchants: ITerminalWithMerchants,
    bridges: WithRef<IBridgeDevice>[],
    merchants: WithRef<IHicapsConnectMerchant>[]
  ): Promise<void> {
    const selected = this._resolveSelectedTerminal(
      terminalWithMerchants,
      bridges,
      merchants
    );
    if (!selected) {
      return;
    }
    await this._hicapsConnect.viewLogs(selected);
  }

  openEditTerminalDialog(
    terminalWithMerchants: ITerminalWithMerchants,
    bridges: WithRef<IBridgeDevice>[],
    merchants: WithRef<IHicapsConnectMerchant>[]
  ): void {
    this._dialog.open<
      HicapsConnectV2EditTerminalDialogComponent,
      IHicapsV2EditTerminalRequest
    >(
      HicapsConnectV2EditTerminalDialogComponent,
      DialogPresets.large({
        data: {
          terminalWithMerchants,
          bridges,
          merchants,
        },
      })
    );
  }

  private async _setTerminalDisabled(
    terminalWithMerchants: ITerminalWithMerchants,
    disabled: boolean
  ): Promise<void> {
    const merchants = Object.fromEntries(
      Object.keys(terminalWithMerchants.terminal.merchants).map(
        (merchantId) => [merchantId, { disabled }]
      )
    );
    try {
      await Firestore.patchDoc(terminalWithMerchants.terminal.ref, {
        merchants,
      });
    } catch {
      this._snackBar.open('Terminal update failed', 'OK', {
        duration: HICAPS_SETTINGS_NOTICE_MS,
      });
    }
  }

  private _resolveSelectedTerminal(
    terminalWithMerchants: ITerminalWithMerchants,
    bridges: WithRef<IBridgeDevice>[],
    merchants: WithRef<IHicapsConnectMerchant>[]
  ): ISelectedHicapsConnectTerminal | undefined {
    const terminal = terminalWithMerchants.terminal;
    const route = HicapsConnectV2Routing.resolveRoute(terminal, bridges);
    if (!route) {
      return;
    }

    const merchantId = Object.entries(terminal.merchants).find(([, edge]) =>
      HicapsConnectMerchant.isEdgeActive(edge)
    )?.[0];

    if (!merchantId) {
      return;
    }

    return HicapsConnectV2Adapter.toSelectedTerminal(
      terminal,
      merchantId,
      merchants.find((merchant) => merchant.merchantId === merchantId),
      route.chosen
    );
  }

  private _buildTerminalWithMerchants(
    terminal: WithRef<IHicapsConnectTerminalV2>,
    bridges: WithRef<IBridgeDevice>[]
  ): ITerminalWithMerchants {
    const isDisabled = HicapsConnectTerminal.isDisabled(terminal);
    const route = isDisabled
      ? undefined
      : HicapsConnectV2Routing.resolveRoute(terminal, bridges);

    return {
      terminalId: terminal.terminalId,
      name: terminal.name,
      preferredBridge: terminal.preferredBridge,
      terminal,
      isActive: !!route,
      isDisabled,
      servingBridge: route?.chosen.bridgeDevice.name,
      usedFallback: route?.usedFallback ?? false,
      bridgeOptions: this._buildBridgeOptions(terminal, bridges),
    };
  }

  private _buildBridgeOptions(
    terminal: WithRef<IHicapsConnectTerminalV2>,
    bridges: WithRef<IBridgeDevice>[]
  ): IBridgeOption[] {
    return compact(
      Object.keys(terminal.bridges).map((bridgeId) =>
        this._toBridgeOption(bridgeId, bridges)
      )
    );
  }

  private _toBridgeOption(
    bridgeId: string,
    bridges: WithRef<IBridgeDevice>[]
  ): IBridgeOption | undefined {
    const bridge = HicapsConnectV2Routing.findBridgeDevice(bridges, bridgeId);
    if (!bridge) {
      return;
    }
    return { id: bridgeId, name: bridge.name || bridgeId, ref: bridge.ref };
  }
}
